import './applianceImage.scss';

import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FsLightbox from 'fslightbox-react';
import { GatsbyImage } from 'gatsby-plugin-image';

import ApplianceArrow from '../../images/icons/appliance-arrow.svg';
import FrameIcon from '../../images/icons/frame.svg';
import { IAppliance } from '../../interfaces';
import { Icon } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import droletLogo from '../../images/icon.png';

const FRAME_INDEX_VALUE = 'frame_index';
export default function ApplianceImage(props: {
    appliance: IAppliance;
    currentLocale: string;
    visaoId?: string;
    MemoizedFrame?: JSX.Element;
}) {
    const { appliance, MemoizedFrame } = props;
    const firstImage =
        appliance?.fields?.images?.[0]?.childImageSharp?.gatsbyImageData;
    const [currentImg, setCurrentImage] = useState(firstImage);
    const [videosData, setVideosData] = useState([]);
    const videoIds = appliance?.attributes?.video_ids?.values.map(
        (videoObject: any) => videoObject[props.currentLocale]
    );
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    let idString = '';
    videoIds?.forEach((id: string) => (idString += `id=${id}&`));

    const { t } = useTranslation('layout');

    useEffect(() => {
        if (idString !== '')
            fetch(
                `https://youtube.googleapis.com/youtube/v3/videos?part=snippet&${idString}key=${process.env.GATSBY_GOOGLE_API_KEY}`
            )
                .then((response) => response.json())
                .then((data) => {
                    if (data?.items?.[0]?.id) {
                        setVideosData(data?.items);
                    } else {
                        console.log('Erreur videosIds');                        
                    }
                });
    }, [idString]);

    const allImages = useMemo(() => {
        if (appliance?.fields?.images) {
            return appliance?.fields?.images?.map(
                (e) => e?.childImageSharp?.gatsbyImageData
            );
        } else {
            return [];
        }
    }, [appliance?.fields?.images]);
    const allVideos =
        useMemo(
            () =>
                videosData?.map(
                    (video: any) => video?.snippet?.thumbnails?.standard?.url
                ),
            [videosData]
        ) ?? [];

    const allImagesVideos = allImages.concat(allVideos);

    const currentIndex = allImagesVideos?.indexOf(
        allImagesVideos?.find((item: any) => item === currentImg)
    );

    const frameIndex = allImagesVideos?.indexOf(
        allImagesVideos?.find((item: any) => item === FRAME_INDEX_VALUE)
    );

    const [isImageOpen, openImage] = useState(false);

    const setNext = () => {
        let nextIndex = currentIndex + 1;
        if (nextIndex >= allImagesVideos.length) {
            nextIndex = 0;
        }
        const imgElm = document.getElementById(`image-single-${nextIndex}`);
        if (imgElm) {
            imgElm.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
        setCurrentImage(allImagesVideos[nextIndex]);
    };
    const setPrevious = () => {
        let previousIndex = currentIndex - 1;
        if (previousIndex < 0) {
            previousIndex = allImagesVideos.length - 1;
        }
        const imgElm = document.getElementById(`image-single-${previousIndex}`);
        if (imgElm) {
            imgElm.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
        setCurrentImage(allImagesVideos[previousIndex]);
    };

    const imagesSource = useMemo(() => {
        if (appliance?.fields?.images) {
            return appliance?.fields?.images?.map(
                (e) =>
                    e?.childImageSharp?.gatsbyImageData?.images?.fallback?.src
            );
        } else {
            return [];
        }
    }, [appliance]);
    const videosSource = useMemo(() => {
        return videosData?.map(
            (video: any) => 'https://www.youtube.com/watch?v=' + video.id
        );
    }, [videosData]);

    const sourceMap = imagesSource.concat(videosSource);

    return (
        <div className='appliance-image-container'>
            <FsLightbox
                toggler={isImageOpen}
                exitFullscreenOnClose={true}
                sources={[...sourceMap]}
                sourceIndex={
                    frameIndex !== -1 && frameIndex <= currentIndex
                        ? currentIndex - 1
                        : currentIndex
                }
            />
            <div
                className='appliance-image'
                onClick={() => openImage(!isImageOpen)}
                onTouchStart={(event) =>
                    setTouchStart(event.targetTouches[0].clientX)
                }
                onTouchMove={(event) =>
                    setTouchEnd(event.targetTouches[0].clientX)
                }
                onTouchEnd={(event) => swipeEvent(event)}
            >
                {GetCurrentImg()}
            </div>

            {allImagesVideos?.length > 1 && (
                <div className='appliance-image-carouselle'>
                    <img
                        src={ApplianceArrow}
                        alt=''
                        className='image-arrow left'
                        onClick={() => setPrevious()}
                    />
                    <div
                        className='appliance-images'
                        onTouchStart={(event) =>
                            setTouchStart(event.targetTouches[0].clientX)
                        }
                        onTouchMove={(event) =>
                            setTouchEnd(event.targetTouches[0].clientX)
                        }
                        onTouchEnd={(event) => swipeEvent(event)}
                    >
                        {getApplianceImages()}
                    </div>
                    <img
                        src={ApplianceArrow}
                        alt=''
                        className='image-arrow right'
                        onClick={() => setNext()}
                    />
                </div>
            )}
        </div>
    );
    function getApplianceImages(): React.ReactNode {
        return (
            <>
                {allImagesVideos?.map((item: any, index: number) => {
                    const isFrame = item === FRAME_INDEX_VALUE;
                    const isVideo = typeof item === 'string' && !isFrame;
                    if (isFrame) {
                        return (
                            <div
                                className={
                                    currentIndex === index
                                        ? 'image-single selected'
                                        : 'image-single'
                                }
                                key={index}
                                id={`image-single-${index}`}
                                onClick={() => {
                                    setCurrentImage(item);
                                }}
                            >
                                <img src={FrameIcon} alt='' />
                            </div>
                        );
                    } else {
                        return (
                            <div
                                className={
                                    currentIndex === index
                                        ? isVideo
                                            ? 'image-single selected video'
                                            : 'image-single selected'
                                        : isVideo
                                        ? 'image-single video'
                                        : 'image-single'
                                }
                                key={index}
                                id={`image-single-${index}`}
                                onClick={() => {
                                    setCurrentImage(item);
                                }}
                            >
                                {isVideo ? (
                                    <img src={item} alt='' />
                                ) : (
                                    <GatsbyImage image={item} alt='' />
                                )}
                            </div>
                        );
                    }
                })}
            </>
        );
    }
    function GetCurrentImg() {
        if (currentImg) {
            if (currentImg === FRAME_INDEX_VALUE) {
                return MemoizedFrame;
            } else if (
                typeof currentImg === 'string' &&
                currentImg !== FRAME_INDEX_VALUE
            ) {
                return (
                    <>
                        <img src={currentImg} alt='' />
                        <Icon
                            d='M44.9,14.5c-0.4-2.2-2.3-3.8-4.5-4.3C37.1,9.5,31,9,24.4,9c-6.6,0-12.8,0.5-16.1,1.2c-2.2,0.5-4.1,2-4.5,4.3C3.4,17,3,20.5,3,25s0.4,8,0.9,10.5c0.4,2.2,2.3,3.8,4.5,4.3c3.5,0.7,9.5,1.2,16.1,1.2s12.6-0.5,16.1-1.2c2.2-0.5,4.1-2,4.5-4.3c0.4-2.5,0.9-6.1,1-10.5C45.9,20.5,45.4,17,44.9,14.5z M19,32V18l12.2,7L19,32z'
                            size='100'
                            fill='darkgrey'
                            viewbox='0 0 50 50'
                        />
                    </>
                );
            } else {
                return <GatsbyImage image={currentImg} alt='' />;
            }
        } else {
            return <img className='placeholder' src={droletLogo} alt='' />;
        }
    }

    function swipeEvent(event: React.TouchEvent<HTMLDivElement>) {
        event.preventDefault();
        let newIndex = currentIndex;
        if (touchStart - touchEnd > 75) {
            newIndex += 1;
            if (newIndex >= allImagesVideos.length) {
                newIndex = 0;
            }
        }
        if (touchStart - touchEnd < -75) {
            newIndex -= 1;
            if (newIndex < 0) {
                newIndex = allImagesVideos.length - 1;
            }
        }
        const imgElm = document.getElementById(`image-single-${newIndex}`);
        if (imgElm) {
            imgElm.scrollIntoView({
                behavior: 'auto',
                block: 'nearest',
            });
        }
        setCurrentImage(allImagesVideos[newIndex]);
    }
}
